import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  PlusIcon,
  DocumentTextIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  DocumentIcon,
  VideoCameraIcon,
  PhotoIcon,
  ArrowDownTrayIcon,
  XCircleIcon,
  CheckCircleIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CloudArrowUpIcon,
  AcademicCapIcon,
  BookOpenIcon,
  ClockIcon,
  UserGroupIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';
import apiService from '../services/api';
import PageHeader from '../components/PageHeader';
import { useToast } from '../components/ToastProvider';

const MaterialManagement = () => {
  const [materials, setMaterials] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // Course dropdown states
  const [courseSearchTerm, setCourseSearchTerm] = useState('');
  const [showCourseDropdown, setShowCourseDropdown] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courseModules, setCourseModules] = useState([]);
  const courseDropdownRef = useRef(null);

  // File upload states
  const [dragActive, setDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
              course_summary: '',
          notes: '',
          youtube_url: '',
          external_url: '',
          module: '',
    material_type: 'document',
    file: null,
    is_public: false,
    order: 1,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const { notify } = useToast();

  // Handle clicking outside course dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (courseDropdownRef.current && !courseDropdownRef.current.contains(event.target)) {
        setShowCourseDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchData = async () => {
    try {
      const [materialsData, coursesData] = await Promise.all([
        apiService.getCourseMaterials(),
        apiService.getMyCourses(),
      ]);
      setMaterials(Array.isArray(materialsData) ? materialsData : (materialsData?.results || []));
      setCourses(Array.isArray(coursesData) ? coursesData : (coursesData?.results || []));
    } catch (error) {
      console.error('Failed to fetch data:', error);
      notify('Failed to load materials', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCourseSelect = async (course) => {
    console.log('Course selected:', course);
    setSelectedCourse(course);
    setShowCourseDropdown(false);
    setCourseSearchTerm('');
    setError('');
    
    try {
      // Fetch modules (getCourseModules already has fallback handling)
      console.log(`Fetching modules for course ID: ${course.id}`);
      const modules = await apiService.getCourseModules(course.id);
      console.log('Modules fetched:', modules);
      setCourseModules(Array.isArray(modules) ? modules : []);
      
      if (Array.isArray(modules) && modules.length > 0) {
        // Modules exist, automatically select the first one (usually "General Materials")
        const generalModule = modules.find(m => m.name === 'General Materials') || modules[0];
        setFormData(prev => ({ ...prev, module: generalModule.id }));
        
        notify(`Course "${course.name || course.title}" selected. Materials will be added to "${generalModule.name}".`, { 
          type: 'success' 
        });
        return;
      }
      
      // No modules found, auto-create "General Materials" module
      console.log('No modules found for course, creating default module...');
      notify('Creating default module for this course...', { type: 'info' });
      
      const payload = {
        name: 'General Materials',
        description: 'Default module for course materials',
        order: 1,
        time_slot: 'Flexible',
        is_active: true,
      };
      
      try {
        const created = await apiService.request(`/courses/courses/${course.id}/modules/`, {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        
        console.log('Module created:', created);
        const newModuleId = created?.id || created?.module?.id;
        
        if (newModuleId) {
          // Refresh modules list
          const updatedModules = await apiService.getCourseModules(course.id);
          setCourseModules(Array.isArray(updatedModules) ? updatedModules : [{ id: newModuleId, name: 'General Materials' }]);
          setFormData(prev => ({ ...prev, module: newModuleId }));
          notify(`Course "${course.name || course.title}" selected. Default module created.`, { type: 'success' });
        } else {
          throw new Error('Module ID not returned from server');
        }
      } catch (createError) {
        console.error('Failed to create module:', createError);
        // If module creation fails, show error but don't block
        setError('Could not auto-create module. Please ask admin to create a module for this course first.');
        notify('Module creation failed. Contact admin to add a module to this course.', { type: 'error' });
      }
      
    } catch (error) {
      console.error('Failed to handle course selection:', error);
      setError('Failed to load course modules. The course may not have any modules yet.');
      notify('Please contact admin to create a module for this course.', { type: 'warning' });
      setFormData(prev => ({ ...prev, module: '' }));
      setCourseModules([]);
    }
  };

  // Removed submitCreateModule - modules are now auto-created

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const formDataToSend = new FormData();
      if (!formData.module) {
        setError('Please select a course first. A module will be automatically assigned.');
        setLoading(false);
        return;
      }
      formDataToSend.append('title', formData.title);
      formDataToSend.append('description', formData.description);
              formDataToSend.append('course_summary', formData.course_summary);
      formDataToSend.append('notes', formData.notes);
      formDataToSend.append('youtube_url', formData.youtube_url);
      formDataToSend.append('external_url', formData.external_url);
        formDataToSend.append('module', formData.module);
        formDataToSend.append('material_type', formData.material_type);
      formDataToSend.append('is_public', formData.is_public.toString());
      formDataToSend.append('order', formData.order);
      if (formData.file) {
        formDataToSend.append('file', formData.file);
      }

      if (editingMaterial) {
        await apiService.updateMaterial(editingMaterial.id, formDataToSend);
        setSuccess('Material updated successfully!');
        notify('Material updated successfully', { type: 'success' });
      } else {
        await apiService.createMaterial(formDataToSend);
        setSuccess('Material uploaded successfully!');
        notify('Material uploaded successfully', { type: 'success' });
      }
      
      setTimeout(() => {
        setShowCreateModal(false);
        setEditingMaterial(null);
        setSelectedCourse(null);
        setFormData({
          title: '',
          description: '',
                  course_summary: '',
        notes: '',
        youtube_url: '',
        external_url: '',
        module: '',
          material_type: 'document',
          file: null,
          is_public: false,
          order: 1,
        });
        setSuccess('');
        fetchData();
      }, 1500);
    } catch (error) {
      console.error('Failed to save material:', error);
      setError('Failed to save material. Please try again.');
      notify('Failed to save material', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (material) => {
    setEditingMaterial(material);
    setFormData({
      title: material.title,
      description: material.description,
      course_summary: material.course_summary || '',
      notes: material.notes || '',
      youtube_url: material.youtube_url || '',
      external_url: material.external_url || '',
      module: material.module,
      material_type: material.material_type,
      file: null,
      is_public: material.is_public || false,
      order: material.order || 1,
    });
    
    // Set selected course for display
    const course = (courses || []).find(c => c.id === material.module_detail?.course);
    if (course) {
      setSelectedCourse(course);
    }
    
    setShowCreateModal(true);
  };

  const handleDelete = async (materialId) => {
    if (window.confirm('Are you sure you want to delete this material?')) {
      try {
        await apiService.deleteMaterial(materialId);
        fetchData();
        notify('Material deleted', { type: 'success' });
      } catch (error) {
        console.error('Failed to delete material:', error);
        notify('Failed to delete material', { type: 'error' });
      }
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'file' ? files[0] : value
    }));
  };

  // Drag and drop handlers
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFormData(prev => ({
        ...prev,
        file: e.dataTransfer.files[0]
      }));
    }
  };

  const getMaterialIcon = (type) => {
    switch (type) {
      case 'document': return DocumentIcon;
      case 'video': return VideoCameraIcon;
      case 'image': return PhotoIcon;
      default: return DocumentTextIcon;
    }
  };

  const getMaterialColor = (type) => {
    switch (type) {
      case 'document': return 'text-blue-600 bg-blue-100';
      case 'video': return 'text-purple-600 bg-purple-100';
      case 'image': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const filteredCourses = (courses || []).filter(course =>
    (course.name || course.title || '').toLowerCase().includes(courseSearchTerm.toLowerCase())
  );

  if (loading && (materials || []).length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-6">
        {/* Aitech Logo Animation */}
        <div className="relative">
          <div className="w-20 h-20 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg animate-pulse">
            <span className="text-2xl font-bold text-white">A</span>
          </div>
          <div className="absolute -inset-1 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl blur opacity-30 animate-pulse"></div>
        </div>
        
        {/* Loading Text */}
        <div className="text-center space-y-2">
          <h3 className="text-lg font-semibold text-gray-900">Loading Materials</h3>
          <p className="text-sm text-gray-500">Please wait while we fetch your learning resources...</p>
        </div>
        
        {/* Modern Loading Animation */}
        <div className="flex space-x-2">
          <div className="w-3 h-3 bg-indigo-600 rounded-full animate-bounce"></div>
          <div className="w-3 h-3 bg-indigo-600 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
          <div className="w-3 h-3 bg-indigo-600 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
        </div>
        
        {/* Progress Bar */}
        <div className="w-64 bg-gray-200 rounded-full h-2 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full animate-pulse"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Materials"
        subtitle="Upload and manage learning resources for your courses"
        actions={(
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Upload Material
          </button>
        )}
      />

      {/* Materials Grid */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {(materials || []).map((material) => {
          const MaterialIcon = getMaterialIcon(material.material_type);
          const getMaterialGradient = (type) => {
            switch (type) {
              case 'document': return 'from-blue-500 to-blue-600';
              case 'video': return 'from-purple-500 to-purple-600';
              case 'image': return 'from-emerald-500 to-emerald-600';
              default: return 'from-gray-500 to-gray-600';
            }
          };
          const getMaterialBgGradient = (type) => {
            switch (type) {
              case 'document': return 'from-blue-50 to-blue-100';
              case 'video': return 'from-purple-50 to-purple-100';
              case 'image': return 'from-emerald-50 to-emerald-100';
              default: return 'from-gray-50 to-gray-100';
            }
          };
          const getMaterialIconBg = (type) => {
            switch (type) {
              case 'document': return 'bg-blue-500';
              case 'video': return 'bg-purple-500';
              case 'image': return 'bg-emerald-500';
              default: return 'bg-gray-500';
            }
          };
          
          return (
            <div
              key={material.id}
              className="group relative overflow-hidden rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-200 hover:shadow-xl hover:ring-2 hover:ring-gray-300 transition-all duration-300 transform hover:-translate-y-1 cursor-pointer"
              role="button"
              tabIndex={0}
              onClick={() => {
                if (material.access_url) {
                  window.open(material.access_url, '_blank', 'noopener,noreferrer');
                }
              }}
              onKeyDown={(e) => {
                if ((e.key === 'Enter' || e.key === ' ') && material.access_url) {
                  window.open(material.access_url, '_blank', 'noopener,noreferrer');
                }
              }}
            >
              {/* Background Gradient */}
              <div className={`absolute inset-0 bg-gradient-to-br ${getMaterialBgGradient(material.material_type)} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
              
              {/* Content */}
              <div className="relative">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-4">
                    <div className={`relative p-3 rounded-2xl ${getMaterialIconBg(material.material_type)} shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                      <MaterialIcon className="h-6 w-6 text-white" />
                      <div className={`absolute inset-0 bg-gradient-to-r ${getMaterialGradient(material.material_type)} rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-gray-900 group-hover:text-gray-800 transition-colors duration-200 truncate">
                        {material.title}
                      </h3>
                      <p className="text-sm text-gray-500 group-hover:text-gray-600 transition-colors duration-200 truncate">
                        {material.course_name || 'No course'}
                      </p>
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleEdit(material); }}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(material.id); }}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                
                <p className="text-sm text-gray-600 mb-6 line-clamp-2 group-hover:text-gray-700 transition-colors duration-200">
                  {material.description}
                </p>

                <div className="flex items-center justify-between">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                    material.material_type === 'document' ? 'bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800' :
                    material.material_type === 'video' ? 'bg-gradient-to-r from-purple-100 to-purple-200 text-purple-800' :
                    material.material_type === 'image' ? 'bg-gradient-to-r from-emerald-100 to-emerald-200 text-emerald-800' :
                    'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-800'
                  }`}>
                    {material.material_type}
                  </span>
                  <div className="flex items-center space-x-3">
                    {material.is_public && (
                      <span className="inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium bg-gradient-to-r from-green-100 to-green-200 text-green-800">
                        Public
                      </span>
                    )}
                    {material.access_url && (
                      <div className="flex space-x-2">
                        <a
                          href={material.access_url}
                          download
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ArrowDownTrayIcon className="h-4 w-4" />
                        </a>
                        <a
                          href={material.access_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <EyeIcon className="h-4 w-4" />
                        </a>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Bottom accent line */}
                <div className="mt-4 h-0.5 bg-gray-200 rounded-full overflow-hidden">
                  <div className={`h-full bg-gradient-to-r ${getMaterialGradient(material.material_type)} rounded-full transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left`} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {(materials || []).length === 0 && (
        <div className="text-center py-16">
          <div className="mx-auto w-20 h-20 bg-gradient-to-br from-blue-100 to-blue-200 rounded-2xl flex items-center justify-center mb-6">
            <DocumentTextIcon className="h-10 w-10 text-blue-500" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-3">No materials uploaded</h3>
          <p className="text-sm text-gray-500 mb-8 max-w-md mx-auto">
            Get started by uploading your first learning material to share with your students.
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white text-sm font-medium rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Upload Material
          </button>
        </div>
      )}

      {/* Professional Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
            {/* Modal Header - Fixed */}
            <div className="flex-shrink-0 p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingMaterial ? 'Edit Material' : 'Upload New Material'}
                </h3>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setEditingMaterial(null);
                    setSelectedCourse(null);
                    setCourseSearchTerm('');
                    setShowCourseDropdown(false);
                    setError('');
                    setSuccess('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Modal Body - Scrollable */}
            <div className="flex-1 overflow-y-auto p-6">
              {success && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2" />
                    <p className="text-sm text-green-800">{success}</p>
                  </div>
                </div>
              )}
              
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center">
                    <XCircleIcon className="h-4 w-4 text-red-500 mr-2" />
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                </div>
              )}
            
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Basic Information Section */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-md font-semibold text-gray-800 mb-4 flex items-center">
                    <DocumentTextIcon className="h-5 w-5 mr-2 text-primary-600" />
                    Material Information
                  </h4>
                                  <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2 text-left">Material Title *</label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      required
                      className="input-field"
                      placeholder="Enter material title (e.g., Introduction to React)"
                      disabled={loading}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2 text-left">Description *</label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      required
                      rows="3"
                      className="input-field"
                      placeholder="Provide a detailed description of this material..."
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2 text-left">Course Summary</label>
                    <textarea
                      name="course_summary"
                      value={formData.course_summary}
                      onChange={handleChange}
                      rows="2"
                      className="input-field"
                      placeholder="Brief summary of how this material fits into the course..."
                      disabled={loading}
                    />
                    <p className="text-xs text-gray-500 mt-1 text-left">Optional: Brief overview of this material's role in the course</p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2 text-left">Notes/Write-up</label>
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleChange}
                      rows="3"
                      className="input-field"
                      placeholder="Notes or write-up to display under the material. Include key points, exercises, or additional information..."
                      disabled={loading}
                    />
                    <p className="text-xs text-gray-500 mt-1 text-left">Optional: These notes will be displayed under the material for students</p>
                  </div>
                </div>
                </div>

                {/* Course Selection Section */}
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="text-md font-semibold text-gray-800 mb-4 flex items-center">
                    <AcademicCapIcon className="h-5 w-5 mr-2 text-blue-600" />
                    Course Assignment
                  </h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2 text-left">Select Course *</label>
                      <div className="relative z-10" ref={courseDropdownRef}>
                        {/* Selected Course Display / Search Input */}
                        <div
                          className={`input-field cursor-pointer flex items-center justify-between ${
                            loading ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                          onClick={() => !loading && setShowCourseDropdown(!showCourseDropdown)}
                        >
                          <div className="flex-1">
                            {selectedCourse ? (
                              <div className="flex items-center justify-between">
                                <div>
                                  <span className="font-medium text-gray-900">{selectedCourse.name || selectedCourse.title}</span>
                                  {selectedCourse.duration_months && (
                                    <span className="text-sm text-gray-500 ml-2">
                                      ({selectedCourse.duration_months} months)
                                    </span>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <span className="text-gray-500">
                                {(courses || []).length === 0 ? 'No courses available' : 
                                 'Search and select a course...'}
                              </span>
                            )}
                          </div>
                          {!loading && (
                            <div className="ml-2 flex-shrink-0">
                              {showCourseDropdown ? (
                                <ChevronUpIcon className="h-4 w-4 text-gray-400" />
                              ) : (
                                <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                              )}
                            </div>
                          )}
                        </div>

                        {/* Dropdown */}
                        {showCourseDropdown && !loading && (
                          <div className="absolute left-0 right-0 z-50 mt-2 w-full bg-white border border-gray-200 rounded-xl shadow-2xl ring-1 ring-black/5 max-h-80 overflow-hidden pl-3 pb-3">
                            {/* Search Input */}
                            <div className="p-3 border-b border-gray-100">
                              <div className="relative">
                                <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                <input
                                  type="text"
                                  placeholder="Search courses..."
                                  value={courseSearchTerm}
                                  onChange={(e) => setCourseSearchTerm(e.target.value)}
                                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                />
                              </div>
                            </div>

                            {/* Course List */}
                            <div className="max-h-60 overflow-y-auto">
                              {filteredCourses.length === 0 ? (
                                <div className="p-4 text-center text-gray-500">
                                  No courses found
                                </div>
                              ) : (
                                filteredCourses.map((course) => (
                                  <div
                                    key={course.id}
                                    className="py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                                    onClick={() => handleCourseSelect(course)}
                                  >
                                    <div className="flex items-start justify-between px-0 pr-4">
                                      <div className="text-left">
                                        <div className="font-medium text-gray-900">
                                          {course.name || course.title}
                                        </div>
                                        {course.duration_months && (
                                          <div className="text-sm text-gray-500">
                                            Duration: {course.duration_months} months
                                          </div>
                                        )}
                                      </div>
                                      <div className="flex items-center space-x-2 text-xs text-gray-400">
                                        <ClockIcon className="h-3 w-3" />
                                        <span>{course.duration_months || 'N/A'} months</span>
                                      </div>
                                    </div>
                                  </div>
                                ))
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Module Selection Section */}
                {selectedCourse && formData.module && (
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h4 className="text-md font-semibold text-gray-800 mb-4 flex items-center">
                      <AcademicCapIcon className="h-5 w-5 mr-2 text-green-600" />
                      Module Assignment
                    </h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-900 mb-2 text-left">Selected Module</label>
                        <div className="p-3 bg-white border border-gray-200 rounded-md">
                          <span className="font-medium text-gray-900">
                            {(() => {
                              // Find the module name from the stored course modules
                              const module = courseModules.find(m => m.id === formData.module);
                              return module ? module.name : 'Module Selected';
                            })()}
                          </span>
                          <p className="text-sm text-gray-500 mt-1">
                            This material will be attached to the selected module
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}


                {/* Material Configuration Section */}
                <div className="bg-green-50 p-4 rounded-lg">
                  <h4 className="text-md font-semibold text-gray-800 mb-4 flex items-center">
                    <BookOpenIcon className="h-5 w-5 mr-2 text-green-600" />
                    Material Configuration
                  </h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2 text-left">Material Type *</label>
                      <select
                        name="material_type"
                        value={formData.material_type}
                        onChange={handleChange}
                        className="input-field"
                        disabled={loading}
                      >
                        <option value="document">📄 Document</option>
                        <option value="video">🎥 Video</option>
                        <option value="image">🖼️ Image</option>
                        <option value="presentation">📊 Presentation</option>
                        <option value="assignment">�� Assignment</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2 text-left">Display Order</label>
                      <input
                        type="number"
                        name="order"
                        value={formData.order}
                        onChange={handleChange}
                        min="1"
                        className="input-field"
                        placeholder="1"
                        disabled={loading}
                      />
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        name="is_public"
                        checked={formData.is_public}
                        onChange={handleChange}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        disabled={loading}
                      />
                      <label className="ml-2 block text-sm font-semibold text-gray-900">
                        Make this material public
                      </label>
                    </div>
                  </div>
                </div>

                {/* File Upload Section */}
                <div className="bg-purple-50 p-4 rounded-lg">
                  <h4 className="text-md font-semibold text-gray-800 mb-4 flex items-center">
                    <CloudArrowUpIcon className="h-5 w-5 mr-2 text-purple-600" />
                    File Upload
                  </h4>
                  
                  <div
                    className={`border-2 border-dashed rounded-lg p-6 text-center ${
                      dragActive ? 'border-primary-500 bg-primary-50' : 'border-gray-300'
                    }`}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                  >
                    {formData.file ? (
                      <div className="space-y-2">
                        <DocumentIcon className="mx-auto h-12 w-12 text-primary-600" />
                        <p className="text-sm font-medium text-gray-900">{formData.file.name}</p>
                        <p className="text-xs text-gray-500">
                          {(formData.file.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, file: null }))}
                          className="text-sm text-red-600 hover:text-red-800"
                        >
                          Remove file
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                        <p className="text-sm text-gray-600">
                          <span className="font-medium text-primary-600 hover:text-primary-500">
                            Click to upload
                          </span>{' '}
                          or drag and drop
                        </p>
                        <p className="text-xs text-gray-500">
                          PDF, DOC, PPT, MP4, JPG, PNG up to 50MB
                        </p>
                        <input
                          type="file"
                          name="file"
                          onChange={handleChange}
                          required={!editingMaterial}
                          className="hidden"
                          id="file-upload"
                          accept=".pdf,.doc,.docx,.ppt,.pptx,.mp4,.avi,.mov,.jpg,.jpeg,.png"
                        />
                        <label
                          htmlFor="file-upload"
                          className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                        >
                          Choose File
                        </label>
                      </div>
                    )}
                  </div>
                </div>

                {/* URL Links Section */}
                <div className="bg-green-50 p-4 rounded-lg">
                  <h4 className="text-md font-semibold text-gray-800 mb-4 flex items-center">
                    <LinkIcon className="h-5 w-5 mr-2 text-green-600" />
                    External Links (Optional)
                  </h4>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2 text-left">YouTube URL</label>
                      <input
                        type="url"
                        name="youtube_url"
                        value={formData.youtube_url}
                        onChange={handleChange}
                        className="input-field"
                        placeholder="https://www.youtube.com/watch?v=..."
                        disabled={loading}
                      />
                      <p className="text-xs text-gray-500 mt-1 text-left">Optional: Link to a YouTube video</p>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2 text-left">External URL</label>
                      <input
                        type="url"
                        name="external_url"
                        value={formData.external_url}
                        onChange={handleChange}
                        className="input-field"
                        placeholder="https://example.com/resource..."
                        disabled={loading}
                      />
                      <p className="text-xs text-gray-500 mt-1 text-left">Optional: Link to external resource</p>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      setEditingMaterial(null);
                      setSelectedCourse(null);
                      setCourseSearchTerm('');
                      setShowCourseDropdown(false);
                      setError('');
                      setSuccess('');
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md disabled:opacity-50"
                    disabled={loading}
                  >
                    {loading ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        {editingMaterial ? 'Updating...' : 'Uploading...'}
                      </div>
                    ) : (
                      editingMaterial ? 'Update Material' : 'Upload Material'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaterialManagement; 